import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./InsuranceReportsWorkspace.css";

const blankFilters = {
  date_from: "", date_to: "", branch: "", plan: "",
  status: "", agent: "", member_type: "",
  search: "", closed_plan_due: "",
};
const money = (value) => typeof value === "number" ? value.toFixed(2) : value;

const LAPSED_POLICY_COLUMNS = [
  "Policy",
  "Policyholder",
  "Phone",
  "Current Plan",
  "Date Lapsed",
  "Days Lapsed",
  "Months Lapsed",
  "Outstanding Balance",
  "Unpaid Bills",
  "Last Payment Date",
  "Closed Plan Due Date",
  "Closed Plan Due?",
  "Branch",
  "Status",
];

const formatCell = (column, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (column === "Outstanding Balance" && typeof value === "number") {
    return value.toFixed(2);
  }
  return money(value);
};

function InsuranceReportsWorkspace() {
  const [reports, setReports] = useState([]);
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(blankFilters);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axiosInstance.get("insurance-reports/")
      .then((response) => {
        setReports(response.data.reports || []);
        setOptions(response.data.filters || {});
        setSelected(response.data.reports?.[0] || null);
      })
      .catch(() => toast.error("Could not load insurance reports."));
  }, []);

  const visible = useMemo(() => reports.filter((report) =>
    `${report.name} ${report.description} ${report.group}`
      .toLowerCase().includes(search.toLowerCase())
  ), [reports, search]);

  const params = () => Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== "")
  );

  const previewReport = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const response = await axiosInstance.get(
        `insurance-reports/${selected.code}/preview/`,
        { params: params() }
      );
      setPreview(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not generate report preview.");
    } finally { setBusy(false); }
  };

  const download = async (type) => {
    if (!selected) return;
    setBusy(true);
    try {
      const response = await axiosInstance.get(
        `insurance-reports/${selected.code}/download/${type}/`,
        { params: params(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);
      if (type === "pdf") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${selected.name.replaceAll(" ", "_")}.csv`;
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch { toast.error(`Could not generate the ${type.toUpperCase()} report.`); }
    finally { setBusy(false); }
  };

  const columns = useMemo(() => {
    if (!preview?.rows?.length) return [];
    if (selected?.code === "LAPSED_POLICIES") {
      const available = new Set(Object.keys(preview.rows[0]));
      const ordered = LAPSED_POLICY_COLUMNS.filter((column) => available.has(column));
      const extras = Object.keys(preview.rows[0]).filter((column) => !ordered.includes(column));
      return [...ordered, ...extras];
    }
    return Object.keys(preview.rows[0]);
  }, [preview, selected]);

  return (
    <section className="reports-page">
      <header className="reports-title">
        <div><span>MANAGEMENT INFORMATION</span><h1>Insurance Reports</h1>
          <p>Preview, print and export operational assurance reports.</p></div>
        <div className="reports-actions">
          <button onClick={previewReport} disabled={busy || !selected}><i className="bi bi-eye" /> Preview</button>
          <button onClick={() => download("pdf")} disabled={busy || !selected}><i className="bi bi-file-earmark-pdf" /> Print PDF</button>
          <button onClick={() => download("csv")} disabled={busy || !selected}><i className="bi bi-filetype-csv" /> Download CSV</button>
        </div>
      </header>

      <div className="reports-layout">
        <aside className="reports-catalogue">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a report" />
          <div>{visible.map((report) => (
            <button key={report.code} className={selected?.code === report.code ? "active" : ""}
              onClick={() => { setSelected(report); setPreview(null); }}>
              <span>{report.group}</span><strong>{report.name}</strong><small>{report.description}</small>
            </button>
          ))}</div>
        </aside>

        <main className="reports-workspace">
          <header><div><span>SELECTED REPORT</span><h2>{selected?.name || "Select a report"}</h2><p>{selected?.description}</p></div></header>
          <div className="report-filters">
            <label>From<input type="date" value={filters.date_from} onChange={(e) => setFilters({...filters,date_from:e.target.value})}/></label>
            <label>To<input type="date" value={filters.date_to} onChange={(e) => setFilters({...filters,date_to:e.target.value})}/></label>
            <label>Branch<select value={filters.branch} onChange={(e) => setFilters({...filters,branch:e.target.value})}><option value="">ALL</option>{(options.branches||[]).map(x=><option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}</select></label>
            <label>Plan<select value={filters.plan} onChange={(e) => setFilters({...filters,plan:e.target.value})}><option value="">ALL</option>{(options.plans||[]).map(x=><option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}</select></label>
            <label>Status<select value={filters.status} onChange={(e) => setFilters({...filters,status:e.target.value})}><option value="">ALL</option>{(options.policy_statuses||[]).map(x=><option key={x}>{x}</option>)}</select></label>
            <label>Agent<select value={filters.agent} onChange={(e) => setFilters({...filters,agent:e.target.value})}><option value="">ALL</option>{(options.agents||[]).map(x=><option key={x.id} value={x.id}>{x.code} · {x.first_name} {x.surname}</option>)}</select></label>
            <label>Member type<select value={filters.member_type} onChange={(e) => setFilters({...filters,member_type:e.target.value})}><option value="">ALL</option>{(options.member_types||[]).map(x=><option key={x}>{x}</option>)}</select></label>
            {selected?.code === "LAPSED_POLICIES" && <>
              <label>Policy / member search<input type="text" value={filters.search} onChange={(e) => setFilters({...filters,search:e.target.value})} placeholder="Policy, name, ID or phone"/></label>
              <label>Closed Plan due<select value={filters.closed_plan_due} onChange={(e) => setFilters({...filters,closed_plan_due:e.target.value})}><option value="">ALL</option><option value="true">DUE ONLY</option><option value="false">NOT DUE</option></select></label>
            </>}
            <button onClick={() => {setFilters(blankFilters);setPreview(null);}}>Clear filters</button>
          </div>

          {!preview ? <div className="report-empty">Choose filters and press Preview.</div> : <>
            <div className="report-summary">{Object.entries(preview.summary||{}).map(([label,value])=><article key={label}><span>{label}</span><strong>{String(value)}</strong></article>)}</div>
            {preview.truncated && <div className="report-warning">Preview shows the first 250 of {preview.total_rows} rows. PDF and CSV include all records.</div>}
            <div className={`report-table ${selected?.code === "LAPSED_POLICIES" ? "lapsed-policy-table" : ""}`}><table><thead><tr>{columns.map(c=><th key={c}>{c}</th>)}</tr></thead><tbody>{preview.rows.map((row,index)=><tr key={index}>{columns.map(c=><td key={c}>{formatCell(c, row[c])}</td>)}</tr>)}</tbody></table></div>
          </>}
        </main>
      </div>
    </section>
  );
}
export default InsuranceReportsWorkspace;
